import React, {PureComponent} from 'react';
import {connect} from 'react-redux';
import MediaQuery from 'react-responsive';

import * as api from '../../lib/api';
import * as flags from '../../lib/flags';
import withUrlState from '../../lib/with-url-state';
import {updateSetting} from '../../state/actions';
import {withUser, getSetting} from '../../lib/user';
import Screen from '../../components/Screen';
import Loading from '../../components/Loading';
import Error from '../../components/Error';
import Content from '../../components/Content';
import SideModal from '../../components/SideModal';
import Button from '../../components/Button';
import Pagination from '../../components/Pagination';
import Form from '../../components/Form';
import Input from '../../components/Input';
import {T} from '../../components/I18n';
import Role from "../../lib/Role";
import HasManagementRole from "../../security/rules/HasManagementRole";
import Welcome from './Welcome';
import NotificationList from './NotificationList';
import SheetActivityList from './SheetActivityList';
import DashboardWidgetSet from './DashboardWidgetSet';
import AlertInsights from './AlertInsights';

const LoadState = {
  COMPLETE: 0,
  INITIAL: 1,
  NEXT_PAGE: 2,
  FAILED: 3
};

class HomeScreen extends PureComponent {
  static LOAD_SIZE = 12;
  static POLL_INTERVAL = 30000; // ms

  state = {
    showIntro: !Boolean(getSetting(this.props.user, 'hide_dashboard_home_intro')),
    users: [],
    publishedForms: [],
    unpublishedForms: [],
    archivedForms: [],
    sheets: [],
    totalSheets: 0,
    loadState: LoadState.INITIAL,
    insights: {
      alerts: []
    },
    filters: {
      open: false
    }
  };

  async componentDidMount() {
    const {credentials, user} = this.props;

    const [users, forms, notifications] = await Promise.all([
      user.role !== "USER" ? api.getAllUsers(credentials) : [],
      api.getCompanyTemplates(credentials),
      api.getNotifications(credentials)
    ]);

    // Categorize forms into 1 of 3 buckets.
    const [publishedForms, unpublishedForms, archivedForms] = forms.reduce(([publishedForms, unpublishedForms, archivedForms], form) => {
      if (form.published && form.current) {
        publishedForms.push(form);
      }
      else if (!form.published && form.current) {
        unpublishedForms.push(form);
      }
      else {
        archivedForms.push(form);
      }

      return [publishedForms, unpublishedForms, archivedForms];
    }, [[], [], []]);

    this.setState({users, publishedForms, unpublishedForms, archivedForms, notifications});
    this.props.onUrlStateChange(this.handleUrlChanged);
    this.handle = setInterval(this.pollForChanges, HomeScreen.POLL_INTERVAL);
    this.loadActivity();

    if(user.role !== "USER") {
      this.loadInsights();
    }
  }

  async componentDidUpdate(prevProps) {
    const {notificationCount, credentials} = this.props;

    if (prevProps.notificationCount !== notificationCount) {
      try {
        const notifications = await api.getNotifications(credentials);
        this.setState({notifications});
      }
      catch (e) {
        // Nothing to do. Failing to load notifications should not interrupt the page.
      }
    }
  }

  componentWillUnmount() {
    clearInterval(this.handle);
  }

  handleUrlChanged = () => {
    this.loadActivity();
    this.loadInsights();
  }

  pollForChanges = () => {
    const {page} = this.props.urlState;

    if (page === 1) {
      this.loadActivity();
    }
  }

  loadActivity = async () => {
    const {page, user, form, sheet} = this.props.urlState;

    try {
      const sheetPage = await this.getRecentSheets(page, user, sheet, form);

      this.setState({
        loadState: LoadState.COMPLETE,
        sheets: sheetPage.content,
        totalSheets: sheetPage.totalElements
      });
    }
    catch(e) {
      this.setState({loadState: LoadState.FAILED});
    }
  }

  loadInsights = async () => {
    let {credentials, urlState: {startDate, endDate}} = this.props;
    let alerts = [];

    try {
      startDate = new Date(parseInt(startDate));
      endDate = new Date(parseInt(endDate));
      alerts = (await api.getAlertInsights(startDate, endDate, credentials)).content;
    }
    catch (e) {
      // Nothing to do. Failure to load insights should not interrupt the page.
    }
    
    this.setState(prevState => ({insights: {...prevState.insights, alerts}}));
  }

  async getRecentSheets(page, userId, sheetName, formId) {
    const {credentials} = this.props;
    return api.filterCompanySheets(formId, userId, sheetName, page - 1, HomeScreen.LOAD_SIZE, 'dates.lastModifiedDate,desc', credentials);
  }

  changePage = page => {
    this.setState({loadState: LoadState.NEXT_PAGE});
    this.props.setUrlState({page});
  }

  toggleFilters = () => {
    this.setState(state => ({
      filters: {
        ...state.filters,
        open: !state.filters.open,
        ...this.props.urlState
      }
    }));
  }

  clearFilters = () => {
    this.toggleFilters();
    this.props.setUrlState(defaultUrlState);
  }

  applyFilters = () => {
    const {filters} = this.state;

    this.toggleFilters();
    this.setState({
      sheets: [],
      loadState: LoadState.INITIAL
    });
    this.props.setUrlState({
      form: filters.form,
      user: filters.user,
      sheet: filters.sheet,
      page: '1'
    });
  }

  setFilterState = nextState => {
    this.setState(prevState => ({
      filters: {
        ...prevState.filters,
        ...nextState
      }
    }));
  }

  handleDismissIntro = forever => {
    const {dispatch, user, credentials} = this.props;

    dispatch(updateSetting(user, 'hide_dashboard_home_intro', 'yes'));
    this.setState({showIntro: false});

    if (forever) {
      api.saveUserSetting(user.id, 'hide_dashboard_home_intro', 'yes', credentials);
    }
  }

  handleInsightsDateChange = ({startDate, endDate}) => {
    const {setUrlState} = this.props;

    setUrlState({
      startDate: '' + startDate.getTime(),
      endDate: '' + endDate.getTime()
    });
  }

  renderScreen = isMobile => {
    const {user, urlState: {page}} = this.props;
    const {sheets, users, notifications, publishedForms, unpublishedForms, archivedForms, totalSheets, showIntro, loadState, filters, insights} = this.state;
    const numForms = publishedForms.length + unpublishedForms.length + archivedForms.length;

    if (showIntro && HasManagementRole(user)) {
      return (
        <Welcome user={user} onDismiss={this.handleDismissIntro} />
      );
    }

    const formsById = [...publishedForms, ...unpublishedForms, ...archivedForms].reduce((acc, form) => {
      acc[form.id] = form;
      return acc;
    }, {});

    // Get and parse alert insights start and end dates.
    let {startDate, endDate} = this.props.urlState;
    startDate = new Date(parseInt(startDate));
    endDate = new Date(parseInt(endDate));

    return (
      <Screen title={<T noTranslate>{user.company.name}</T>} showTitle={isMobile || user.role === "USER"} windowTitle={user.company.name} activeLink="Dashboard">
        {loadState === LoadState.INITIAL ? (
          <Loading fullScreen message="Loading..." />
        ) : loadState === LoadState.FAILED ? (
          <Error fullScreen message="There was a problem! Please try again." />
        ) : (
          <>
          {(user.role !== "USER") &&
            <AlertInsights 
              alerts={insights.alerts} 
              formsById={formsById}
              startDate={startDate} 
              endDate={endDate}
              onChange={this.handleInsightsDateChange}
            />}
            <Content>
              {/* Currently, the ANALYST role doesn't receive any notifications */
              user.role !== Role.ANALYST && user.role !== "USER" && flags.hasNotifications(user) && (
                <NotificationList notifications={notifications} />
              )}
              <SheetActivityList
                sheets={sheets}
                options={<Button tertiary onClick={this.toggleFilters}>Filter Sheets</Button>}
                pagination={
                  <Pagination
                    mini
                    disabled={loadState === LoadState.NEXT_PAGE}
                    page={parseInt(page)}
                    itemsPerPage={HomeScreen.LOAD_SIZE}
                    totalItems={totalSheets}
                    onClick={this.changePage}
                  />
                }
              />
              {flags.isOn(user, 'PINNED_WIDGETS_ENABLED') && (
                <DashboardWidgetSet templates={publishedForms} />
              )}
            </Content>
          </>
        )}
        <Form onSubmit={this.applyFilters}>
          <SideModal
            title="Filters"
            open={filters.open}
            onClose={this.toggleFilters}
            options={[
              <Button type="submit">Apply Filters</Button>,
              <Button secondary onClick={this.clearFilters}>Clear Filters</Button>
            ]}>
            <SideModal.Content>
              <Form.Group>
                <Input
                  id="sheet-name-filter"
                  label="Filter by Sheet Name"
                  type="text"
                  value={filters.sheet}
                  onChange={e => this.setFilterState({sheet: e.target.value})}
                />
              </Form.Group>
              {numForms > 0 && (
                <Form.Group>
                  <Input
                    id="sheet-form-filter"
                    label="Filter by Form"
                    type="select"
                    value={filters.form}
                    onChange={e => this.setFilterState({form: e.target.value})}>
                    <option value="">Choose a form to filter...</option>
                    {publishedForms.sort(sortForms()).length > 0 && (
                      <optgroup label="Published Forms">
                        {publishedForms.map(form => (
                          <T element="option" noTranslate key={form.id} value={form.id}>{form.name}</T>
                        ))}
                      </optgroup>
                    )}
                    {unpublishedForms.sort(sortForms()).length > 0 && (
                      <optgroup label="Unpublished Forms">
                        {unpublishedForms.map(form => (
                          <T element="option" noTranslate key={form.id} value={form.id}>{form.name}</T>
                        ))}
                      </optgroup>
                    )}
                    {archivedForms.sort(sortForms()).length > 0 && (
                      <optgroup label="Archived Forms">
                        {archivedForms.map(form => (
                          <T element="option" noTranslate key={form.id} value={form.id}>{form.name}</T>
                        ))}
                      </optgroup>
                    )}
                  </Input>
                </Form.Group>
              )}
              <Form.Group>
                <Input
                  id="sheet-user-filter"
                  label="Filter by User"
                  type="select"
                  value={filters.user}
                  onChange={e => this.setFilterState({user: e.target.value})}>
                  <option value="">Choose a user to filter...</option>
                  {users.sort(sortUsers()).map(user => (
                    <T element="option" noTranslate key={user.id} value={user.id}>{user.userName}</T>
                  ))}
                </Input>
              </Form.Group>
            </SideModal.Content>
          </SideModal>
        </Form>
      </Screen>
    );
  }

  render() {
    return (
      <MediaQuery maxWidth={800}>{this.renderScreen}</MediaQuery>
    );
  }
}

function sortForms() {
  return (a, b) => {
    return a.name.trim().toLowerCase().localeCompare(b.name.trim().toLowerCase());
  };
}

function sortUsers() {
  return (a, b) => {
    return a.userName.trim().toLowerCase().localeCompare(b.userName.trim().toLowerCase());
  };
}

const defaultUrlState = {
  sheet: '',
  user: '',
  form: '',
  page: '1',
  startDate: '' + new Date(new Date().getTime() - 6*24*60*60*1000).getTime(),
  endDate: '' + new Date().getTime()
};

export default connect(state => ({
  notificationCount: state.notificationCount
}))(withUser(withUrlState(defaultUrlState)(HomeScreen)));
